import { Checkbox } from "@kobalte/core/checkbox";
import { Dialog } from "@kobalte/core/dialog";
import { debounce } from "@solid-primitives/scheduled";
import { A } from "@solidjs/router";
import { PaperclipIcon } from "@storytell/bsky-icons";
import {
  CreateViewRequestConfig,
  DeleteAssetRequestConfig,
  GenerateSignedUrlRequestConfig,
  InvitationsSyncRequestConfig,
  UpdateViewRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { dispatchGlobalEvent } from "@storytell/events";
import type { Asset, AssetId, Concept, ConceptId, LabelId, ViewId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "@storytell/ui/dropdown";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import {
  type ColumnDef,
  type Header,
  type SortingState,
  createSolidTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
} from "@tanstack/solid-table";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import {
  TbAlertTriangle,
  TbArrowRight,
  TbCaretDown,
  TbCaretUp,
  TbCircleCheckFilled,
  TbEye,
  TbFilterPlus,
  TbHelpCircle,
  TbLoader2,
  TbMessage2,
  TbTag,
  TbTrash,
} from "solid-icons/tb";
import { TbCheck } from "solid-icons/tb";
import {
  type Accessor,
  For,
  Match,
  type Setter,
  Show,
  Switch,
  batch,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import { twMerge } from "tailwind-merge";
import { MoveAssetsModal } from "~/components/assets/MoveAssetsModal";
import { FilterGroup } from "~/components/filtering/Filters";
import {
  type FilterBuilder,
  type UIFilterExpression,
  isPositiveOperator,
  uiFiltersToServerFilters,
} from "~/components/filtering/filterBuilder";
import { TbStackPlus } from "~/components/knowledge/TbStackPlus";
import type { EntitySelection } from "~/components/knowledge/useEntitySelection";
import { AssetLabelsRow } from "~/components/labels/AssetLabelsRow";
import { BulkAssetLabelPicker } from "~/components/labels/BulkAssetLabelPicker";
import { LabelPicker } from "~/components/labels/LabelPicker";
import { lifecycleStateGrouped } from "~/components/utils/lifecycleStateMappers";
import { useWire } from "~/wire";
import { useAppUI } from "./AppUIContext";
dayjs.extend(localizedFormat);

export const NavigationAssetsPane = (props: {
  search: string;
  setSearch: (s: string) => void;
  buildFilterFunction: (item: Asset | Concept) => boolean;
  setLabelFilter: (labelId: string) => void;
  selection: EntitySelection;
  filterBuilder: FilterBuilder<Asset | Concept>;
  hasViewChanges: boolean;
  activeView: ViewId | "all" | "new";
  editView: boolean;
  setEditView: (edit: boolean) => void;
  assetToHighlight: string;
  setAssetToHighlight: (s: string) => void;
  labelToFilterBy: string;
  setLabelToFilterBy: (s: string) => void;
  preserveFiltersForNewView: Accessor<boolean>;
  setPreserveFiltersForNewView: Setter<boolean>;
  mount?: Node;
}) => {
  const wire = useWire();
  // createEffect(
  //   on(
  //     () => props.labelToFilterBy,
  //     (labelId) => {
  //       if (!labelId) return;
  //       props.filterBuilder.setFilter({
  //         operator: "and",
  //         filter: (asset) => filterAssetByLabel(asset, labelId),
  //         filterId: labelId,
  //       });
  //       props.setLabelToFilterBy("");
  //     },
  //   ),
  // );

  // Sorting based on initial scope to bring the in-scope assets to the top
  // but also prevent them from shifting around as the user adds/removes assets
  const assets = createMemo(() => wire.assets.search(props.search).filter(props.buildFilterFunction));

  // Default sorting: by last updated, descending (newest first)
  const [sorting, setSorting] = createSignal<SortingState>([{ id: "Last Updated", desc: true }]);

  const ui = useAppUI();
  const [deleting, setDeleting] = createSignal<Record<string, boolean>>({});
  const [viewing, setViewing] = createSignal<Record<string, boolean>>({});
  const [deleteModalOpen, setDeleteModalOpen] = createSignal(false);
  const [singleDeleteModalOpen, setSingleDeleteModalOpen] = createSignal(false);
  const [assetToDelete, setAssetToDelete] = createSignal<string | null>(null);
  const [moveModalOpen, setMoveModalOpen] = createSignal(false);

  // Get current project and check if it's shared
  const currentProject = () => {
    const userContext = wire.userContext.get();
    if (!userContext) return undefined;
    return wire.projects.get(userContext.projectId);
  };

  // Fetch invitations to determine if project is shared
  const [invitations] = createResource(
    () => wire.auth.isReady() && currentProject()?.id,
    async () => {
      const client = useStorytellClient();
      const request = InvitationsSyncRequestConfig.withBearerToken(wire.auth.token());
      const response = await client.fetch(request, undefined, undefined);

      if (response.isErr()) {
        console.error("Failed to fetch invitations", response.error);
        return [];
      }

      const project = currentProject();
      if (!project) return [];

      // Filter invitations for the current project
      return Object.values(response.value.invites).filter((invite) => invite.projectId === project.id);
    },
    {
      initialValue: [],
    },
  );

  const isProjectShared = () => {
    const projectInvitations = invitations();
    return projectInvitations && projectInvitations.length > 0;
  };

  const onBulkDelete = () => {
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const ids = Object.entries(props.selection.selected())
      .filter(([_, value]) => value)
      .map(([id]) => id);

    if (ids.length === 0) return;

    const client = useStorytellClient();
    setDeleting((prev) => ({ ...prev, ...props.selection.selected() }));

    try {
      const result = await Promise.all(
        ids.map(async (id) => {
          const requestBuilder = DeleteAssetRequestConfig.withBearerToken(wire.auth.token());
          return { id: id as AssetId, result: await client.fetch(requestBuilder, { id: id as AssetId }, undefined) };
        }),
      );

      for (const asset of result) {
        batch(() => {
          if (asset.result.isOk()) {
            wire.rels.onAssetDeleted(asset.id);
          }
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      const deleted = ids.reduce(
        (acc, id) => {
          acc[id] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      );

      setDeleting((prev) => ({ ...prev, ...deleted }));
      setDeleteModalOpen(false);
    }
  };

  const onSingleDelete = (id: string) => {
    setAssetToDelete(id);
    setSingleDeleteModalOpen(true);
  };

  const handleConfirmSingleDelete = async () => {
    const id = assetToDelete();
    if (!id) return;

    const httpClient = useStorytellClient();
    const authToken = wire.auth.token();

    setDeleting((prev) => ({ ...prev, [id]: true }));
    try {
      const requestBuilder = DeleteAssetRequestConfig.withBearerToken(authToken);

      const result = await httpClient.fetch(requestBuilder, { id: id as AssetId }, undefined);

      if (result.isErr()) {
        console.error(result.error);
        throw result.error;
      }

      // On successful deletion, notify the wire
      wire.rels.onAssetDeleted(id as AssetId);
    } catch (error) {
      console.error(error);
    } finally {
      setDeleting((prev) => ({ ...prev, [id]: false }));
      props.selection.setSelected((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setSingleDeleteModalOpen(false);
      setAssetToDelete(null);
    }
  };

  const onViewAsset = async (id: string) => {
    const client = useStorytellClient();
    const requestConfig = GenerateSignedUrlRequestConfig.withBearerToken(wire.auth.token());
    const authToken = wire.auth.token();

    if (!authToken) {
      console.error("No authentication token available");
      return;
    }

    setViewing((prev) => ({ ...prev, [id]: true }));
    try {
      const response = await client.fetch(requestConfig, { id: id as AssetId }, undefined);

      if (response.isErr()) {
        console.error(response.error);
        throw response.error;
      }

      if (response.isOk()) {
        window.open(response.value.url, "_blank");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setViewing((prev) => ({ ...prev, [id]: false }));
    }
  };

  // Table columns definition
  const columns: ColumnDef<Asset>[] = [
    {
      id: "bulk-select",
      header: () => (
        <Checkbox
          checked={props.selection.allSelected(assets())}
          indeterminate={props.selection.someSelected(assets()) && !props.selection.allSelected(assets())}
          aria-label={props.selection.allSelected(assets()) ? "Deselect all" : "Select all"}
          onChange={(value) => props.selection.toggleAll(value, assets())}
          class="flex items-center justify-center"
        >
          <Checkbox.Input />
          <Checkbox.Control
            data-some-selected={props.selection.someSelected(assets()) && !props.selection.allSelected(assets())}
            class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[some-selected=true]:bg-black/30 dark:data-[some-selected=true]:bg-white/30 data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors"
          >
            <Show when={props.selection.allSelected(assets()) || props.selection.someSelected(assets())}>
              <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
            </Show>
          </Checkbox.Control>
        </Checkbox>
      ),
      cell: (info) => {
        const id = () => info.row.original.id;
        const checked = () => !!props.selection.selected()[id()];
        return (
          <Checkbox
            checked={checked()}
            aria-label={checked() ? "Deselect asset" : "Select asset"}
            onChange={(value) => {
              props.selection.setSelected((prev) => ({ ...prev, [id()]: value }));
            }}
            class="flex items-center justify-center"
          >
            <Checkbox.Input />
            <Checkbox.Control class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors">
              <Show when={checked()}>
                <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
              </Show>
            </Checkbox.Control>
          </Checkbox>
        );
      },
      footer: (props) => props.column.id,
      enableSorting: false,
    },
    {
      header: "Name",
      accessorFn: (row) => row.filename,
      cell: (info) => {
        let btn!: HTMLButtonElement;
        onMount(() => {
          const leave = debounce(() => {
            const parent = btn.parentElement;
            if (!parent) return;
            parent.style.removeProperty("max-width");
          }, 300);
          const enter = () => {
            const parent = btn.parentElement;
            if (!parent) return;
            leave.clear();
            const width = parent.getBoundingClientRect().width - 38;
            parent.style.setProperty("max-width", `${width}px`);
          };

          btn.addEventListener("mouseenter", enter);
          btn.addEventListener("mouseleave", leave);
          onCleanup(() => {
            btn.removeEventListener("mouseenter", enter);
            btn.removeEventListener("mouseleave", leave);
          });
        });
        return (
          <button
            ref={btn}
            onClick={() => {
              dispatchGlobalEvent("add-items-to-prompt-scope", { ids: [info.row.original.id] });
            }}
            type="button"
            class="group/btn relative text-left cursor-pointer font-medium text-primary-text max-w-full line-clamp-1 wrap-anywhere py-1 px-2 border border-transparent rounded-lg hover:border-primary-border transition-all"
          >
            <span class="absolute left-2 opacity-0 group-hocus/btn:opacity-100 transition-all duration-100">
              Chat with
            </span>
            <span class="group-hocus/btn:ml-17 transition-all duration-100 text-transparent bg-clip-text bg-primary-text group-hocus/btn:bg-gradient-to-r group-hocus/btn:from-violet-600 group-hocus/btn:to-blue-600 group-hocus/btn:dark:from-violet-400 group-hocus/btn:dark:to-blue-400">
              <span class="hidden group-hocus/btn:inline">@</span>
              {String(info.getValue())}
            </span>
          </button>
        );
      },
      footer: (props) => props.column.id,
      enableSorting: true,
    },
    {
      header: "Labels",
      cell: (info) => (
        <AssetLabelsRow
          assetId={info.row.original.id}
          mount={panelRef}
          setSearch={props.setSearch}
          addFilter={(label) => {
            // filterBuilder.setFilter({
            //   operator: "and",
            //   filter: (asset) => filterAssetByLabel(asset, label),
            //   filterId: label,
            // });
          }}
          selectedAssets={props.selection.selectedIds()}
        />
      ),
    },
    // TODO: @andi, add file size
    // {
    //   header: "File Size",
    //   accessorFn: (row) => row.fileSize,
    //   cell: (info) => <span class="whitespace-nowrap">{formatFileSize(info.getValue() as number | bigint)}</span>,
    //   footer: (props) => props.column.id,
    // },
    {
      header: "Last Updated",
      accessorFn: (row) => new Date(row.modifiedAt).getTime(),
      cell: (info) => {
        const date = () => {
          return dayjs(info.row.original.modifiedAt).format("LLL");
        };
        return <span class="text-xs">{date()}</span>;
      },
      footer: (props) => props.column.id,
      enableSorting: true,
    },
    {
      header: "Status",
      cell: (info) => {
        const value = () => lifecycleStateGrouped(info.row.original.status.state);
        return (
          <Show
            when={value() === "insufficient credits"}
            fallback={
              <span class="flex items-center gap-1 capitalize text-xs">
                <Switch>
                  <Match when={value() === "uploading" || value() === "processing"}>
                    <TbLoader2 class="w-3 h-3 animate-spin group-hocus/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
                  </Match>
                  <Match when={value() === "error"}>
                    <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hocus/btn:scale-hover group-active/btn:scale-active transition-transform" />
                  </Match>
                  <Match when={value() === "ready"}>
                    <TbCircleCheckFilled class="w-3 h-3 group-hocus/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
                  </Match>
                </Switch>
                {value()}
              </span>
            }
          >
            <StTooltip
              portalProps={{
                mount: props.mount,
              }}
              contentProps={{
                class: "z-50",
              }}
              placement="top"
              content={
                <>
                  <p>Storytell ran out of credits to process this asset.</p>
                  <p>
                    Please{" "}
                    <A onClick={() => ui.actions.closeSecondaryPane()} class="text-brand underline" href="/pricing">
                      top up your credits
                    </A>{" "}
                    and try again.
                  </p>
                </>
              }
            >
              <StTooltipTrigger class="flex items-center gap-1 capitalize text-xs">
                <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hocus/btn:scale-hover group-active/btn:scale-active transition-transform" />
                Insufficient credits
              </StTooltipTrigger>
            </StTooltip>
          </Show>
        );
      },
      footer: (props) => props.column.id,
    },

    {
      header: " ",
      accessorFn: (row) => row.id,
      cell: (info) => {
        const id = () => String(info.getValue());
        return (
          <div class="flex items-center gap-2 w-max">
            <StButton
              class="bg-tile hover:bg-tile-hover"
              onClick={() => onViewAsset(id())}
              icon={TbEye}
              loading={viewing()[id()]}
            />
            <Show when={wire.assets.canDelete(info.row.original.id)}>
              <StButton
                class="bg-tile hover:bg-tile-hover"
                onClick={() => onSingleDelete(id())}
                icon={TbTrash}
                loading={deleting()[id()]}
              />
            </Show>
          </div>
        );
      },
      footer: (props) => props.column.id,
    },
  ];

  // Create the table instance
  const table = createSolidTable({
    get data() {
      return assets();
    },
    columns,
    state: {
      get sorting() {
        return sorting();
      },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  // Keyboard handler for sorting
  function handleHeaderKeyDown(e: KeyboardEvent, header: Header<Asset, unknown>) {
    if (header.column.getCanSort() && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      const handler = header.column.getToggleSortingHandler();
      if (handler) handler(e);
    }
  }

  let panelRef!: HTMLDivElement;
  // let inputRef!: HTMLInputElement;
  // onMount(() => {
  //   inputRef.focus();
  // });

  const [bulkLabelPickerOpen, setBulkLabelPickerOpen] = createSignal(false);

  // Filter Labels functionality (ported from Filters.tsx)
  const [search, setSearch] = createSignal("");
  const [open, setOpen] = createSignal(false);
  const [tempData, setTempData] = createSignal({
    selectedLabels: [] as LabelId[],
  });

  const filterLabelsBySearch = () => wire.labels.search(search());

  const getUsedLabelIds = () => {
    const usedIds = new Set<LabelId>();
    props.filterBuilder?.state.filter.groups.forEach((group) => {
      group.filters.forEach((filter) => {
        if (filter.kind === "label") {
          usedIds.add(filter.id as LabelId);
        }
      });
    });
    return usedIds;
  };

  const getAvailableLabels = () => {
    const usedIds = getUsedLabelIds();
    return filterLabelsBySearch().filter((label) => !usedIds.has(label.id));
  };

  const hasAvailableLabels = () => getAvailableLabels().length > 0;

  // Helper functions for Clear and Save as View functionality
  const viewData = () => {
    if (props.activeView === "new" || props.activeView === "all") return null;
    return wire.views.get(props.activeView);
  };

  const updateView = async (viewId: ViewId, name?: string) => {
    const client = useStorytellClient();
    const request = UpdateViewRequestConfig.withBearerToken(wire.auth.token());
    const response = await client.fetch(
      request,
      { id: viewId },
      {
        id: viewId,
        name: name || wire.views.get(viewId)?.name || "",
        description: "",
        filters: uiFiltersToServerFilters(props.filterBuilder.state.filter),
      },
    );

    if (response.isOk()) {
      wire.views.set(response.value.id, { ...response.value, userId: wire.auth.user()?.id || "" });
      props.setEditView(false);
    }
  };

  const createView = async (name: string) => {
    const client = useStorytellClient();
    const request = CreateViewRequestConfig.withBearerToken(wire.auth.token());

    const response = await client.fetch(request, undefined, {
      name: name,
      description: "",
      filters: uiFiltersToServerFilters(props.filterBuilder.state.filter),
    });

    if (response.isOk()) {
      wire.views.set(response.value.id, { ...response.value, userId: wire.auth.user()?.id || "" });
      wire.knowledgeUi.setActiveView(response.value.id);
    }
  };

  // Helper functions for filter display
  const operator = () => props.filterBuilder?.state.filter.operator || "and";

  const activeOption = () => {
    const o = operator();
    if (!props.filterBuilder?.state.filter.groups.length) return undefined;

    if (props.filterBuilder.state.filter.groups.length <= 1) {
      if (isPositiveOperator(o)) {
        return "include";
      }
      return "exclude";
    }

    if (o === "and") {
      return (
        <>
          include <span class="font-bold">all</span> of
        </>
      );
    }
    if (o === "or") {
      return (
        <>
          include <span class="font-bold">any</span> of
        </>
      );
    }
    if (o === "nand") {
      return (
        <>
          exclude if <span class="font-bold">all</span>
        </>
      );
    }

    return (
      <>
        exclude if <span class="font-bold">any</span> of
      </>
    );
  };

  const addLabelFilter = (labelId: string, groupIndex: number) => {
    if (!props.filterBuilder) return;
    const filter: UIFilterExpression<Asset | Concept> = {
      id: labelId as LabelId,
      kind: "label" as const,
      filter: (entity: Asset | Concept) => {
        if (entity.id.startsWith("asset_")) {
          return wire.rels.assetHasLabel(entity.id as AssetId, labelId as LabelId);
        }
        if (entity.id.startsWith("concept_")) {
          return wire.rels.conceptHasLabel(entity.id as ConceptId, labelId as LabelId);
        }
        return true;
      },
    };
    props.filterBuilder.addFilter(filter, groupIndex);
  };

  const removeLabelFilter = (labelId: string, groupIndex: number) => {
    if (!props.filterBuilder) return;
    props.filterBuilder.removeFilter(labelId, groupIndex);
  };

  return (
    <div ref={panelRef} class="px-2">
      {/* Search Box and Filter Labels Button */}
      <div class="px-4 mb-4">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-3">
            <input
              type="text"
              value={props.search}
              onInput={(e) => props.setSearch(e.currentTarget.value)}
              placeholder="Search Assets"
              class="min-w-[300px] px-3 py-2 border border-primary-border rounded-lg bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              aria-label="Search assets"
            />
            <StPopover
              contentProps={{ class: "z-50" }}
              placement="bottom-start"
              noArrow
              gutter={10}
              open={open()}
              onOpenChange={(open) => {
                setOpen(open);
                setTempData({ selectedLabels: [] });
                requestAnimationFrame(() => {
                  if (!open) {
                    if (props.filterBuilder?.state.filter.groups.at(-1)?.filters.length === 0) {
                      props.filterBuilder.removeFilterGroup(props.filterBuilder.state.filter.groups.length - 1);
                    }
                  }
                });
              }}
              content={
                <LabelPicker
                  title="Choose Labels to Filter"
                  disableCreate
                  search={search()}
                  setSearch={setSearch}
                  labels={getAvailableLabels()}
                  selectedLabels={tempData().selectedLabels}
                  onSelectLabel={(l) => {
                    batch(() => {
                      if (tempData().selectedLabels.length === 0) {
                        props.filterBuilder?.addFilterGroup("and", []);
                      }
                      setTempData((d) => ({ ...d, selectedLabels: [...d.selectedLabels, l.id] }));
                      addLabelFilter(l.id, (props.filterBuilder?.state.filter.groups.length || 1) - 1);
                    });
                  }}
                  onDeselectLabel={(l) => {
                    batch(() => {
                      setTempData((d) => ({ ...d, selectedLabels: d.selectedLabels.filter((id) => id !== l.id) }));
                      removeLabelFilter(l.id, (props.filterBuilder?.state.filter.groups.length || 1) - 1);
                      if (tempData().selectedLabels.length === 0) {
                        props.filterBuilder?.removeFilterGroup(
                          (props.filterBuilder?.state.filter.groups.length || 1) - 1,
                        );
                      }
                    });
                  }}
                  onCreateLabel={async () => {}}
                />
              }
            >
              <StPopoverTrigger
                as={StButton}
                icon={TbFilterPlus}
                noBorder
                iconClass="size-3.5 stroke-[2.5px]"
                aria-label="Add Filter"
                class="group"
              >
                <span class="group-hover:hidden">Add a Filter</span>
                <span class="hidden group-hover:inline">Filter Labels</span>
              </StPopoverTrigger>
            </StPopover>
          </div>

          {/* Add files button - only show for All Knowledge view */}
          <Show when={props.activeView === "all" && wire.grants.canCreate("cp.assets")}>
            <button
              type="button"
              class="border-2 border-dashed border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-950/20 rounded-lg px-4 py-2 flex items-center justify-center gap-2 hover:bg-blue-100 dark:hover:bg-blue-950/30 transition-colors cursor-pointer min-w-[300px]"
              onClick={() => {
                dispatchGlobalEvent("open-file-picker", undefined);
                wire.knowledgeUi.close();
              }}
            >
              <PaperclipIcon class="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span class="text-blue-700 dark:text-blue-300 font-medium text-sm">+ Add files to All Knowledge</span>
            </button>
          </Show>

          {/* Learn how to add files - for custom views */}
          <Show when={props.activeView !== "all"}>
            <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-950/20 rounded-lg px-4 py-2 flex items-center justify-center gap-2 min-w-[300px]">
              <TbHelpCircle class="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <span class="text-gray-700 dark:text-gray-300 font-medium text-sm">
                <a
                  href="http://go.storytell.ai/learn-add-files-to-data-views"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
                >
                  Learn how
                </a>{" "}
                to add files to {wire.views.get(props.activeView as ViewId)?.name || "this view"}
              </span>
            </div>
          </Show>
        </div>

        {/* Selected Assets Actions */}
        <Show when={props.selection.selectedIds().length}>
          {(len) => (
            <div class="flex items-center gap-3 sticky -top-2 z-10 bg-panel py-2 mt-4">
              <StButton
                noColor
                class="shrink-0 bg-brand hover:bg-brand-hover text-on-brand"
                icon={TbMessage2}
                onClick={() => {
                  dispatchGlobalEvent("add-items-to-prompt-scope", { ids: props.selection.selectedIds() });
                }}
                loading={Object.values(deleting()).some(Boolean)}
              >
                Chat with {len()} Asset{len() === 1 ? "" : "s"}
              </StButton>
              {/* <StButton icon={TbStackPlus} class="group/btn shrink-0 dark:bg-tile dark:hover:bg-tile-hover">
                  Create Collection
                </StButton> */}
              <Show when={wire.grants.canUpdate("cp.labels")}>
                <StPopover
                  open={bulkLabelPickerOpen()}
                  onOpenChange={setBulkLabelPickerOpen}
                  contentProps={{ class: "z-50" }}
                  portalProps={{
                    mount: panelRef,
                  }}
                  placement="bottom-start"
                  noArrow
                  gutter={10}
                  content={
                    <BulkAssetLabelPicker
                      onLabelCreated={() => {
                        setBulkLabelPickerOpen(false);
                        dispatchGlobalEvent("refresh-sync", undefined);
                      }}
                      onClose={() => setBulkLabelPickerOpen(false)}
                      mount={panelRef}
                      selected={props.selection.selectedIds() as AssetId[]}
                    />
                  }
                >
                  <StPopoverTrigger as={StButton} icon={TbTag} class="shrink-0 dark:bg-tile dark:hover:bg-tile-hover">
                    Change Labels
                  </StPopoverTrigger>
                </StPopover>
              </Show>
              <Show when={wire.grants.canUpdate("cp.assets") && wire.projects.all().length > 1}>
                <StButton
                  icon={TbArrowRight}
                  class="shrink-0 dark:bg-tile dark:hover:bg-tile-hover"
                  onClick={() => setMoveModalOpen(true)}
                >
                  Move {len()} Asset{len() === 1 ? "" : "s"}
                </StButton>
              </Show>
              <Show when={wire.grants.canDelete("cp.assets")}>
                <StButton
                  noColor
                  class="shrink-0"
                  icon={TbTrash}
                  onClick={onBulkDelete}
                  loading={Object.values(deleting()).some(Boolean)}
                >
                  Delete {len()} Asset{len() === 1 ? "" : "s"}
                </StButton>
              </Show>
            </div>
          )}
        </Show>

        {/* Filter Groups Display with Clear and Save buttons */}
        <Show when={props.filterBuilder?.state.filter.groups.length > 0}>
          <div class="mt-3 flex items-center gap-2 flex-wrap">
            <Show when={activeOption()}>
              {(option) => (
                <button
                  type="button"
                  class="text-xs border flex items-center gap-1 border-primary-border rounded-md px-1.5 py-1 hover:bg-panel-hover cursor-pointer whitespace-nowrap"
                  onClick={() => {
                    props.filterBuilder.changeGlobalOperator(operator() === "and" ? "or" : "and");
                  }}
                >
                  {option()}
                  <TbArrowRight class="stroke-2" />
                </button>
              )}
            </Show>
            <For each={props.filterBuilder.state.filter.groups}>
              {(group, groupIndex) => (
                <>
                  {groupIndex() > 0 && (
                    <span class="text-xs text-secondary-text font-medium px-1">
                      {operator() === "and" ? "and" : "or"}
                    </span>
                  )}
                  <FilterGroup
                    onRemove={() => props.filterBuilder.removeFilterGroup(groupIndex())}
                    onSelectOperator={(op) => {
                      props.filterBuilder.changeGroupOperator(op, groupIndex());
                    }}
                    onSelectLabel={(l) => addLabelFilter(l.id, groupIndex())}
                    onDeselectLabel={(l) => removeLabelFilter(l.id, groupIndex())}
                    group={group}
                    groupIndex={groupIndex()}
                  />
                </>
              )}
            </For>

            {/* Clear and Save as View buttons on the same line */}
            <div class="w-px h-3 bg-primary-border" />
            <Show when={props.activeView !== "new"}>
              <StButton
                noBorder
                noColor
                class="py-1"
                onClick={() => {
                  props.filterBuilder.resetFilter();
                  wire.knowledgeUi.setActiveView("all");
                }}
              >
                Clear
              </StButton>
            </Show>

            <Switch>
              <Match when={props.hasViewChanges && props.activeView === "all" && wire.grants.canCreate("cp.views")}>
                <StButton
                  class="py-1"
                  icon={TbStackPlus}
                  onClick={() => {
                    props.setPreserveFiltersForNewView(true);
                    wire.knowledgeUi.setActiveView("new");
                  }}
                >
                  Save as Collection
                </StButton>
              </Match>
              <Match when={props.hasViewChanges && wire.views.canUpdate(props.activeView as ViewId)}>
                <StDropdown
                  gutter={10}
                  placement="bottom-end"
                  contentProps={{ class: "z-50" }}
                  content={
                    <div>
                      <StDropdownItem
                        onClick={() => updateView(props.activeView as ViewId, viewData()?.name)}
                        class="px-2 py-1 hover:bg-tile-hover cursor-pointer rounded-md"
                      >
                        Save to {viewData()?.name}
                      </StDropdownItem>
                      <StDropdownItem
                        class="px-2 py-1 hover:bg-tile-hover cursor-pointer rounded-md"
                        onClick={() => {
                          props.setPreserveFiltersForNewView(true);
                          wire.knowledgeUi.setActiveView("new");
                        }}
                      >
                        Save as new Collection
                      </StDropdownItem>
                    </div>
                  }
                >
                  <StDropdownTrigger as={StButton} class="py-1" icon={TbStackPlus}>
                    Save
                  </StDropdownTrigger>
                </StDropdown>
              </Match>
            </Switch>
          </div>
        </Show>
      </div>

      <div
        class={twMerge(
          "rounded-lg border border-primary-border flex-auto overflow-auto max-h-[calc(100vh-24vh)]",
          props.selection.selectedIds().length > 0 && "max-h-[calc(100vh-30vh)]",
        )}
      >
        <div class="grid grid-cols-[40px_minmax(20rem,max-content)_auto_auto_auto_auto] text-sm min-w-full">
          {/* Header */}
          <For each={table.getHeaderGroups()}>
            {(headerGroup) => (
              <For each={headerGroup.headers}>
                {(header) => (
                  <div class="text-xs px-3 py-2 font-medium text-left text-primary-text whitespace-nowrap sticky top-0 z-10 bg-canvas border-b border-primary-border flex items-center">
                    <Show when={!header.isPlaceholder}>
                      <div
                        class={
                          header.column.getCanSort() ? "cursor-pointer select-none flex items-center gap-1" : undefined
                        }
                        tabIndex={header.column.getCanSort() ? 0 : undefined}
                        role={header.column.getCanSort() ? "button" : undefined}
                        aria-pressed={header.column.getIsSorted() ? "true" : "false"}
                        onClick={header.column.getToggleSortingHandler()}
                        onKeyDown={(e) => handleHeaderKeyDown(e as KeyboardEvent, header)}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <Show when={header.column.getIsSorted() === "asc"}>
                          <TbCaretUp class="inline-block w-4 h-4 text-brand" />
                        </Show>
                        <Show when={header.column.getIsSorted() === "desc"}>
                          <TbCaretDown class="inline-block w-4 h-4 text-brand" />
                        </Show>
                      </div>
                    </Show>
                  </div>
                )}
              </For>
            )}
          </For>

          {/* Body */}
          <Show
            when={table.getRowModel().rows.length > 0}
            fallback={<div class="col-span-6 text-center p-4 text-muted-text">No assets found</div>}
          >
            <For each={table.getRowModel().rows}>
              {(row) => {
                const isHighlighted = () => props.assetToHighlight === row.original.id;
                let ref!: HTMLDivElement;
                createEffect(() => {
                  if (isHighlighted()) {
                    ref?.scrollIntoView({ behavior: "smooth" });
                    setTimeout(() => {
                      props.setAssetToHighlight("");
                    }, 3000);
                  }
                });
                return (
                  <For each={row.getVisibleCells()}>
                    {(cell, col) => (
                      <div
                        ref={col() === 0 ? ref : undefined}
                        classList={{
                          "sticky left-0 bg-gradient-to-r from-panel via-panel to-transparent": col() === 0,
                          "sticky right-0 bg-gradient-to-l from-panel via-panel to-transparent":
                            col() === columns.length - 1,
                        }}
                        class={twMerge(
                          "px-3 py-2 border-b border-muted-border text-secondary-text flex items-center",
                          isHighlighted() && "bg-yellow-500/20",
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    )}
                  </For>
                );
              }}
            </For>
          </Show>
        </div>
      </div>

      {/* Delete Asset Confirmation Modal */}
      <Dialog open={deleteModalOpen()} onOpenChange={setDeleteModalOpen}>
        <Dialog.Portal mount={panelRef}>
          <Dialog.Overlay class="fixed inset-0 z-[9999] bg-black/50 dark:bg-white/10" />
          <div class="fixed z-[9999] inset-0 flex items-center justify-center p-4">
            <Dialog.Content class="max-w-md w-full rounded-xl bg-white dark:bg-gray-900 p-6 shadow-2xl border border-gray-200 dark:border-gray-700">
              <div class="space-y-4">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                    Delete {props.selection.selectedIds().length} Asset
                    {props.selection.selectedIds().length === 1 ? "" : "s"}
                  </h3>
                  <div class="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mt-2">
                    <div class="flex items-center gap-2">
                      <span class="text-lg">🛑</span>
                      <p class="text-red-800 dark:text-red-200 text-sm">
                        {isProjectShared() ? (
                          <>
                            These assets will be deleted for all users of{" "}
                            <span class="font-bold">{currentProject()?.name || "this project"}</span> and cannot be
                            recovered.
                          </>
                        ) : (
                          <>
                            These assets will be deleted from{" "}
                            <span class="font-bold">{currentProject()?.name || "this project"}</span> and cannot be
                            recovered.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="flex justify-end gap-3">
                  <Dialog.CloseButton class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                    Cancel
                  </Dialog.CloseButton>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </Dialog.Content>
          </div>
        </Dialog.Portal>
      </Dialog>

      {/* Single Asset Delete Confirmation Modal */}
      <Dialog open={singleDeleteModalOpen()} onOpenChange={setSingleDeleteModalOpen}>
        <Dialog.Portal mount={panelRef}>
          <Dialog.Overlay class="fixed inset-0 z-[9999] bg-black/50 dark:bg-white/10" />
          <div class="fixed z-[9999] inset-0 flex items-center justify-center p-4">
            <Dialog.Content class="max-w-md w-full rounded-xl bg-white dark:bg-gray-900 p-6 shadow-2xl border border-gray-200 dark:border-gray-700">
              <div class="space-y-4">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Delete Asset</h3>
                  <div class="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mt-2">
                    <div class="flex items-center gap-2">
                      <span class="text-lg">🛑</span>
                      <p class="text-red-800 dark:text-red-200 text-sm">
                        {isProjectShared() ? (
                          <>
                            This asset will be deleted for all users of{" "}
                            <span class="font-bold">{currentProject()?.name || "this project"}</span> and cannot be
                            recovered.
                          </>
                        ) : (
                          <>
                            This asset will be deleted from{" "}
                            <span class="font-bold">{currentProject()?.name || "this project"}</span> and cannot be
                            recovered.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="flex justify-end gap-3">
                  <Dialog.CloseButton class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                    Cancel
                  </Dialog.CloseButton>
                  <button
                    type="button"
                    onClick={handleConfirmSingleDelete}
                    class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </Dialog.Content>
          </div>
        </Dialog.Portal>
      </Dialog>

      {/* Move Assets Modal */}
      <Show when={currentProject()?.id}>
        {(projectId) => (
          <MoveAssetsModal
            open={moveModalOpen()}
            onOpenChange={setMoveModalOpen}
            selectedAssetIds={props.selection.selectedIds() as AssetId[]}
            currentProjectId={projectId()}
            onSuccess={() => {
              // Clear selection after successful move
              props.selection.setSelected({});
            }}
          />
        )}
      </Show>
    </div>
  );
};

// Format file size to human readable string
function formatFileSize(bytes: bigint | number) {
  let size = typeof bytes === "bigint" ? Number(bytes) : typeof bytes === "number" ? bytes : 0;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}
